import { useEffect, useState, useRef, lazy, Suspense } from "react";
import { useNavigate, useLocation, Routes, Route, Navigate } from "react-router";
import { ErrorBoundary } from "../components/ErrorBoundary";
// Lazy load heavy components to improve initial load time
const Contacts = lazy(() => import("./Contacts"));
const Manage = lazy(() => import("./Manage"));
const Admin = lazy(() => import("./Admin"));
const PortalApp = lazy(() => import("../portal"));
const QuickDial = lazy(() => import("./QuickDial"));
import AdminConsentCallback from "./AdminConsentCallback";
import { RoleSwitcher } from "../components/RoleSwitcher";
import { ActionMenu, type ActionMenuItem } from "../components/ActionMenu";
import { LanguageSelector } from "../components/LanguageSelector";
import { HELP_PANEL_WIDTH, HelpPanel, isHelpCenterConfigured } from "../components/help/HelpPanel";
import { OnboardingGuide } from "../components/OnboardingGuide";
import { ThemeToggle } from "../components/ThemeToggle";
import { TrialBanner } from "../components/TrialBanner";
import { LandingPage } from "../components/LandingPage";
import { setAuthHeaderProvider, getMe, ME_REFRESH_EVENT, type Me } from "../lib/api";
import { readStoredValue, writeStoredValue } from "../lib/safeStorage";
import { useAuth } from "../contexts/AuthContext";
import PortalGuard from "../portal/PortalGuard";
import { usePermissions } from "../hooks/usePermissions";
import { useCurrentSite } from "../hooks/useCurrentSite";
import { initTelemetry, setTelemetryUser, trackPageView } from "../lib/telemetry";
import { trackVisit, trackUsage } from "../lib/usage";
import {
  Button,
  Card,
  CardHeader,
  Text,
  Spinner,
  Tooltip,
  makeStyles,
  shorthands,
  tokens
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import {
  Phone20Regular,
  People20Regular,
  Settings20Regular,
  Shield20Regular,
  SignOut20Regular,
  BookOpen20Regular
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  root: {
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground1
  },
  loginCard: {
    ...shorthands.padding(tokens.spacingVerticalXXL),
    textAlign: 'center',
    maxWidth: '400px',
    ...shorthands.margin('0', 'auto'),
    marginTop: tokens.spacingVerticalXXXL
  },
  menuSettings: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap(tokens.spacingHorizontalS),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  menuSettingsControls: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  loadingContainer: {
    ...shorthands.padding(tokens.spacingVerticalL),
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL
  },
  loadingCard: {
    ...shorthands.padding(tokens.spacingVerticalL)
  }
});

export default function App() {
  console.log('App component rendering...');
  const { t } = useTranslation();
  
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = usePermissions(); // Use permissions hook that respects role switcher
  const [showHelp, setShowHelp] = useState(false);
  const [me, setMe] = useState<Me | null>(null);
  // Locally hide the onboarding guide once dismissed, so it disappears immediately without
  // waiting for a `me` refetch. The guide reappears the next time the user navigates to a
  // primary page (contacts or quick) unless permanently suppressed via localStorage.
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const [initialRouteSet, setInitialRouteSet] = useState(false);
  // Gate the authenticated UI until the start-page check has resolved, so we
  // never flash QuickDial before redirecting users whose start page is Contacts.
  const [startupResolved, setStartupResolved] = useState(false);
  // Track whether the viewport is mobile-sized. The Admin page is too wide to
  // render usefully on mobile, so its menu entry is hidden below this breakpoint.
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 767px)').matches
  );
  const { site: currentSite } = useCurrentSite();
  const styles = useStyles();
  
  // Get current "tab" from URL
  const currentTab = location.pathname.startsWith('/portal') ? 'portal'
    : location.pathname === '/contacts' ? 'contacts' 
    : location.pathname === '/manage' ? 'manage'
    : location.pathname === '/admin' ? 'admin'
    : 'quick';

  // localStorage key scoped to the user so "do not show again" is per-user/tenant.
  const onboardingSuppressedKey = me?.userId ? `qd_onboarding_suppressed_${me.userId}` : null;

  // Re-show the guide whenever the user navigates to a primary page (contacts or quick),
  // provided they have not permanently suppressed it or completed onboarding.
  useEffect(() => {
    if (
      me?.showOnboarding &&
      (location.pathname === '/contacts' || location.pathname === '/')
    ) {
      const key = me.userId ? `qd_onboarding_suppressed_${me.userId}` : null;
      const suppressed = key ? !!readStoredValue(key) : false;
      if (!suppressed) {
        setOnboardingDismissed(false);
      }
    }
  }, [location.pathname, me?.showOnboarding, me?.userId]);

  // Keep the mobile-viewport flag in sync with the current window size.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(max-width: 767px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobileViewport(e.matches);
    setIsMobileViewport(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // Initialize telemetry on app load
  useEffect(() => {
    initTelemetry();
    
    // Fetch dynamic app registrations from API
    (async () => {
      try {
        const { config } = await import('../lib/config');
        await config.fetchAppRegistrations();
      } catch (error) {
        console.warn('Failed to fetch app registrations:', error);
      }
    })();
  }, []);
  
  // Set initial route based on user's quickDialAsStartPage setting.
  // We resolve `me` (and any redirect) before revealing the authenticated UI via
  // `startupResolved`, so the start page is decided in the background behind a
  // loading animation rather than flashing QuickDial first.
  useEffect(() => {
    if (auth.isAuthenticated && !initialRouteSet && location.pathname === '/') {
      getMe().then(meData => {
        setMe(meData);
        if (meData.quickDialAsStartPage === "off") {
          navigate('/contacts', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
        setInitialRouteSet(true);
        setStartupResolved(true);
      }).catch(err => {
        console.error('Failed to get user data for initial route:', err);
        navigate('/', { replace: true }); // Default to quick on error
        setInitialRouteSet(true);
        setStartupResolved(true);
      });
    } else if (auth.isAuthenticated && location.pathname !== '/') {
      // Deep-linked to a non-root route: still load `me` so onboarding / nav state is available.
      getMe()
        .then(setMe)
        .catch((err) => console.error('Failed to get user data:', err))
        .finally(() => {
          setInitialRouteSet(true);
          setStartupResolved(true);
        });
    }
  }, [auth.isAuthenticated, initialRouteSet, location.pathname, navigate]);

  useEffect(() => {
    const refreshMe = () => {
      getMe(true)
        .then(setMe)
        .catch((err) => console.error('Failed to refresh user data:', err));
    };

    window.addEventListener(ME_REFRESH_EVENT, refreshMe);
    return () => window.removeEventListener(ME_REFRESH_EVENT, refreshMe);
  }, []);
  
  // Set telemetry user context when authenticated
  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      setTelemetryUser(
        auth.user.userId || 'unknown',
        auth.user.tenantId || 'unknown',
        auth.user.tenantRole || 'User'
      );
    }
  }, [auth.isAuthenticated, auth.user]);
  
  // Record a single usage "visit" per authenticated session load (persisted to Cosmos via /usage,
  // distinct from the console-only App Insights telemetry stub above).
  const visitTracked = useRef(false);
  // Record a usage "access" each time the user lands on the QuickDial speed-dial or contact-list
  // surface. Keyed on the tab itself (not currentSite) so switching sites within QuickDial or
  // re-rendering does not inflate the count.
  const lastSurfaceRef = useRef<string | null>(null);
  useEffect(() => {
    if (!auth.isAuthenticated) {
      visitTracked.current = false;
      return;
    }

    if (!visitTracked.current) {
      visitTracked.current = true;
      trackVisit();
    }
  }, [auth.isAuthenticated]);

  // Track page/tab changes
  useEffect(() => {
    if (auth.isAuthenticated) {
      trackPageView(currentTab ?? "unknown", {
        siteName: currentSite?.name,
        siteId: currentSite?.id
      });
    }
  }, [currentTab, auth.isAuthenticated, currentSite]);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      lastSurfaceRef.current = null;
      return;
    }
    if (currentTab !== 'quick' && currentTab !== 'contacts') return;
    if (lastSurfaceRef.current === currentTab) return;
    lastSurfaceRef.current = currentTab;
    trackUsage(currentTab === 'quick' ? 'quickdial-view' : 'contactlist-view');
  }, [currentTab, auth.isAuthenticated]);
  
  // Debug logging for role checking
  console.log('Auth State:', {
    isAuthenticated: auth.isAuthenticated,
    user: auth.user,
    roles: auth.roles,
    canAccessSites: permissions.canAccessSites(),
    canAccessAdmin: permissions.canAccessAdmin(),
    isUsingRoleOverride: permissions.isUsingRoleOverride,
    effectiveRole: permissions.effectiveRole
  });

  // Admin-consent popup redirect target. Render it unauthenticated and outside the app shell
  // so the popup can relay its result to the opener and close itself.
  if (location.pathname === '/admin-consent-callback') {
    return <AdminConsentCallback />;
  }

  return (
    <ErrorBoundary>
      <div className={styles.root}>
        {!auth.isAuthenticated ? (
          !auth.teamsDetected ? (
            // Teams-context detection hasn't settled yet. Until it does, `isInTeams`
            // is still its default `false`, so we must not commit to either the
            // Teams card or the web LandingPage — showing the LandingPage here
            // flashes it inside Teams before detection flips `isInTeams` true.
            // Render the same neutral spinner used for start-page resolution.
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '100vh',
              gap: tokens.spacingVerticalL
            }}>
              <Spinner size="huge" label={t('loading.quickDial')} />
            </div>
          ) : (auth.isInTeams || auth.isEmbedded) ? (
            // Host SSO login card. `isEmbedded` is checked as well as `isInTeams` so
            // that being framed by an M365 host is on its own enough to keep us off
            // the browser-only landing page, whatever host detection concluded — the
            // landing page's MSAL popup cannot work inside a host webview.
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '100vh',
              padding: '20px',
              backgroundColor: '#f5f5f5'
            }}>
              <Card className={styles.loginCard} style={{
                padding: '40px',
                textAlign: 'center',
                maxWidth: '400px',
                margin: '0 auto',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                border: '1px solid #e1e1e1'
              }}>
                <Text as="h2" size={600} weight="semibold" style={{ marginBottom: '16px', color: '#323130' }}>
                  {t('app.teamsTitle')}
                </Text>
                <Text style={{ marginBottom: '24px', color: '#605e5c' }}>
                  {t('auth.authenticatingWithTeams')}
                </Text>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    auth.login().catch(console.error);
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    auth.login().catch(console.error);
                  }}
                  style={{
                    backgroundColor: '#0078d4',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    minWidth: '200px',
                    minHeight: '44px',
                    transition: 'background-color 0.2s ease',
                    fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'rgba(0,0,0,0)'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#106ebe'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#0078d4'}
                >
                  {t('auth.continueWithTeams')}
                </button>
              </Card>
            </div>
          ) : (
            // Web users: Exobe-branded landing page
            <LandingPage
              onSignIn={() => auth.login().catch(console.error)}
              isLoading={auth.isLoading || auth.isMsalInteracting}
            />
          )
        ) : !startupResolved ? (
          // Resolve the start-page decision in the background before showing the
          // app shell, so users aren't shown QuickDial only to be redirected.
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            gap: tokens.spacingVerticalL
          }}>
            <Spinner size="huge" label={t('loading.quickDial')} />
          </div>
        ) : (
          <div style={{
            position: 'relative',
            height: '100%',
            // Make room for the docked help panel so the page reflows beside it instead of under it.
            marginRight: showHelp && !isMobileViewport ? HELP_PANEL_WIDTH : undefined,
          }}>
            {/* Trial notice across the whole app, above every page. The portal has its own audience. */}
            {currentTab !== 'portal' && (
              <TrialBanner
                trialEndsAt={me?.trialEndsAt}
                tenantRole={me?.tenantRole}
                onChoosePlan={() => navigate('/admin?tab=subscription')}
              />
            )}

            {/* First-run onboarding guide for new tenants (the tenant's first user and any admin). */}
            {me?.showOnboarding && !onboardingDismissed && (
              <OnboardingGuide
                organizationName={null}
                userName={me.name}
                isTenantAdmin={me.tenantRole === 'TenantAdmin'}
                onNavigateAway={() => setOnboardingDismissed(true)}
                onOpenIntegrations={() => {
                  // Close for this session only; the guide returns until setup is finished.
                  setOnboardingDismissed(true);
                  navigate('/admin?tab=integrations');
                }}
                onDismiss={() => {
                  setOnboardingDismissed(true);
                  setMe((prev) => (prev ? { ...prev, showOnboarding: false } : prev));
                }}
                onSuppressForever={() => {
                  if (onboardingSuppressedKey) {
                    writeStoredValue(onboardingSuppressedKey, 'true');
                  }
                  setOnboardingDismissed(true);
                }}
              />
            )}

            {/* Navigation Menu — slide-in side panel (Voicemail Manager style) */}
            <ActionMenu
              title={t('app.name')}
              description={t('app.navigation')}
              triggerLabel={t('app.menu')}
              {...(showHelp && !isMobileViewport
                ? { triggerRight: `calc(${HELP_PANEL_WIDTH} + ${tokens.spacingHorizontalM})` }
                : {})}
              header={
                <div className={styles.menuSettings}>
                  <Tooltip content={t('app.settingsHint')} relationship="description" withArrow>
                    <Text weight="semibold" size={300}>{t('app.settings')}</Text>
                  </Tooltip>
                  <div className={styles.menuSettingsControls}>
                    <ThemeToggle variant="segmented" />
                    <LanguageSelector />
                  </div>
                </div>
              }
              footer={
                // Role switcher ("View as") — only for actual admins. Pinned to
                // the bottom of the menu.
                auth.user?.tenantRole === 'TenantAdmin' ? (
                  <RoleSwitcher />
                ) : undefined
              }
              items={(() => {
                const navItems: ActionMenuItem[] = [];
                if (me && me.quickDialAsStartPage !== 'off') {
                  navItems.push({
                    key: 'quick',
                    label: t('app.quickDial'),
                    icon: Phone20Regular,
                    active: currentTab === 'quick',
                    onSelect: () => navigate('/'),
                  });
                }
                navItems.push({
                  key: 'contacts',
                  label: t('app.contacts'),
                  icon: People20Regular,
                  active: currentTab === 'contacts',
                  onSelect: () => navigate('/contacts'),
                });
                navItems.push({
                  key: 'manage',
                  label: t('app.manage'),
                  icon: Settings20Regular,
                  visible: permissions.canAccessSites(),
                  active: currentTab === 'manage',
                  onSelect: () => navigate('/manage'),
                });
                navItems.push({
                  key: 'admin',
                  label: t('app.admin'),
                  icon: Shield20Regular,
                  // Admin page is too wide for mobile, so hide it from the menu there.
                  visible: permissions.canAccessAdmin() && !isMobileViewport,
                  active: currentTab === 'admin',
                  onSelect: () => navigate('/admin'),
                });
                navItems.push({
                  key: 'help',
                  label: t('app.help'),
                  icon: BookOpen20Regular,
                  visible: isHelpCenterConfigured(),
                  onSelect: () => setShowHelp(true),
                });
                navItems.push({
                  key: 'signout',
                  label: t('app.signOut'),
                  icon: SignOut20Regular,
                  visible: !auth.isInTeams && !auth.isEmbedded,
                  onSelect: () => auth.logout(),
                });
                return navItems;
              })()}
            />

            {/* Role Override Banner */}
            {permissions.isUsingRoleOverride && (
              <div style={{
                position: 'fixed',
                top: '60px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 100,
                backgroundColor: tokens.colorBrandBackground,
                color: 'white',
                padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
                borderRadius: tokens.borderRadiusMedium,
                boxShadow: tokens.shadow16,
                display: 'flex',
                alignItems: 'center',
                gap: tokens.spacingHorizontalS,
                fontSize: tokens.fontSizeBase300,
                fontWeight: tokens.fontWeightSemibold,
              }}>
                <span>🔍</span>
                <span>{t('preview.viewingAs', { role: permissions.effectiveRole === 'Manager' ? t('preview.manager') : t('preview.user') })}</span>
              </div>
            )}

            {/* Page Content */}
            <Routes>
              <Route path="/" element={
                me?.quickDialAsStartPage === 'off'
                  ? <Navigate to="/contacts" replace />
                  : (
                    <Suspense fallback={
                      <div className={styles.loadingContainer}>
                        <Spinner size="large" label={t('loading.quickDial')} />
                      </div>
                    }>
                      <QuickDial goContacts={() => navigate('/contacts')} />
                    </Suspense>
                  )
              } />
              <Route path="/portal/*" element={
                <Suspense fallback={
                  <div className={styles.loadingContainer}>
                    <Spinner size="large" label={t('loading.portal')} />
                  </div>
                }>
                  <PortalGuard>
                    <PortalApp />
                  </PortalGuard>
                </Suspense>
              } />
              <Route path="/contacts" element={
                <Suspense fallback={
                  <div className={styles.loadingContainer}>
                    <Spinner size="large" label={t('loading.contacts')} />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: tokens.spacingHorizontalM }}>
                      {Array.from({ length: 6 }).map((_, i) => (
                        <Card key={i} className={styles.loadingCard}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, marginBottom: tokens.spacingVerticalM }}>
                            <div style={{ width: '40px', height: '40px', backgroundColor: tokens.colorNeutralBackground3, borderRadius: tokens.borderRadiusCircular }}></div>
                            <div style={{ flex: 1 }}>
                              <div style={{ height: '16px', width: '120px', backgroundColor: tokens.colorNeutralBackground3, borderRadius: tokens.borderRadiusSmall, marginBottom: tokens.spacingVerticalXS }}></div>
                              <div style={{ height: '14px', width: '160px', backgroundColor: tokens.colorNeutralBackground3, borderRadius: tokens.borderRadiusSmall }}></div>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                }>
                  <Contacts currentSite={currentSite} />
                </Suspense>
              } />
              <Route path="/manage" element={
                !auth.user ? (
                  <div className={styles.loadingContainer}>
                    <Spinner size="large" label={t('loading.generic')} />
                  </div>
                ) : permissions.canAccessSites() ? (
                  <Suspense fallback={
                    <div className={styles.loadingContainer}>
                      <Spinner size="large" label={t('loading.manage')} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                        {Array.from({ length: 4 }).map((_, i) => (
                          <div key={i} style={{ height: '64px', backgroundColor: tokens.colorNeutralBackground3, borderRadius: tokens.borderRadiusMedium }}></div>
                        ))}
                      </div>
                    </div>
                  }>
                    <Manage />
                  </Suspense>
                ) : <Navigate to="/" replace />
              } />
              <Route path="/admin" element={
                !auth.user ? (
                  <div className={styles.loadingContainer}>
                    <Spinner size="large" label={t('loading.generic')} />
                  </div>
                ) : permissions.canAccessAdmin() ? (
                  <div style={{ padding: tokens.spacingHorizontalL, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXL }}>
                    <Suspense fallback={
                      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                        <Spinner size="large" label={t('loading.admin')} />
                        <div style={{ height: '128px', backgroundColor: tokens.colorNeutralBackground3, borderRadius: tokens.borderRadiusMedium }}></div>
                      </div>
                    }>
                      <Admin />
                    </Suspense>
                  </div>
                ) : <Navigate to="/" replace />
              } />
            </Routes>
            
            <HelpPanel
              open={showHelp}
              onClose={() => setShowHelp(false)}
              // Only a tenant admin can preview another role, so only their view role is trusted here.
              role={permissions.actualRole === 'TenantAdmin' ? permissions.effectiveRole : permissions.actualRole}
              fullScreen={isMobileViewport}
            />
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}
